import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {getAllActiveFranchise} from '@app/services/franchise';
import KpiStrip from './components/KpiStrip';
import ReportFilters, {FranchiseOption} from './components/ReportFilters';
import {
  ChartCard,
  ReportHorizontalBarChart,
  DIRECTION_COLOURS
} from './components/ReportChart';
import useReportExport from './hooks/useReportExport';
import {reportTableStyles} from './components/tableStyles';
import {humanise} from './components/labels';
import {findReportBySlug} from './reportCatalogue';

/**
 * LEAD3 — Lead Velocity.
 *
 * Deliberately restrained. The transition data is thin (largest sample n=37,
 * most under 20) and the pipeline runs backwards about as often as forwards, so
 * this does not draw a tidy stage-by-stage funnel timeline that would imply
 * precision the data cannot support.
 *
 * Instead: every median carries its sample size, thin ones are marked, moves are
 * coloured by direction rather than forced into a linear order, and the stalled
 * list — the genuinely actionable half — gets equal billing.
 */

const SLUG = 'lead-velocity';
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR - i);

const DIRECTION_LABEL: Record<string, string> = {
  forward: 'Forward',
  won: 'Converted',
  backward: 'Went backwards',
  exit: 'Dropped out'
};

const LeadVelocity = () => {
  const definition = findReportBySlug(SLUG);

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<FranchiseOption[]>([]);

  const [filters, setFilters] = useState<ReportFilterValues>({
    mode: 'year',
    year: CURRENT_YEAR,
    period: 'full',
    page: 1,
    limit: 25
  });
  const [applied, setApplied] = useState<ReportFilterValues>(filters);

  const {exportToExcel, exporting} = useReportExport('Lead Velocity');

  const load = useCallback(async (active: ReportFilterValues) => {
    setLoading(true);
    setError(null);
    const response = await getReport(SLUG, active);
    if (response && response.success) {
      setPayload(response);
    } else {
      setPayload(null);
      setError(response?.message || 'The report could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  useEffect(() => {
    (async () => {
      const response = await getAllActiveFranchise();
      if (response?.success === true && Array.isArray(response.collections)) {
        setFranchises(
          response.collections.map((f: any) => ({value: f.id, label: f.name}))
        );
      }
    })();
  }, []);

  const collections = payload?.collections;
  const byTransition = collections?.series?.by_transition || [];
  const detail = collections?.detail;

  const columns = [
    {name: 'Student', selector: (row: any) => row.student || '---', wrap: true},
    {
      name: 'Subject',
      width: '130px',
      // Rows are per student per subject, so the same student can appear once
      // for each subject they enquired about.
      cell: (row: any) =>
        row.subject ? <span className="badge badge-info">{row.subject}</span> : '---'
    },
    {name: 'Franchise', selector: (row: any) => row.franchise || '---', wrap: true},
    {name: 'Stuck at', selector: (row: any) => humanise(row.status) || '---', wrap: true},
    {name: 'Last moved', selector: (row: any) => row.last_moved || '---', width: '120px'},
    {
      name: 'Days stalled',
      width: '130px',
      right: true,
      cell: (row: any) => (
        <span
          className={
            row.days_stalled > 180
              ? 'text-danger font-weight-bold'
              : row.days_stalled > 90
                ? 'text-warning font-weight-bold'
                : ''
          }>
          {row.days_stalled}
        </span>
      )
    }
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{definition?.name || 'Lead Velocity'}</h1>
              <p className="text-muted mb-0">{definition?.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/reports">Reports</Link></li>
                <li className="breadcrumb-item active">Lead Velocity</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <ReportFilters
            values={filters}
            onChange={setFilters}
            onApply={() => setApplied({...filters, limit: applied.limit, page: 1})}
            onReset={(defaults) => {
              setFilters(defaults);
              setApplied(defaults);
            }}
            onExport={() => exportToExcel(payload, SLUG, applied)}
            exporting={exporting}
            franchises={franchises}
            years={YEARS}
            intervalLabel={collections?.filters_applied?.interval_label}
            show={{franchise: true, subject: true, leadStudent: true, stuckAt: true}}
            disabled={loading}
          />

          {error && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          <KpiStrip kpis={collections?.kpis || []} loading={loading} />

          <div className="row">
            <div className="col-12">
              <ChartCard
                title="How long each move takes"
                subtitle="Median days between statuses. Sample sizes are small, so each bar carries its own n — treat anything under 20 as indicative only."
                loading={loading}
                isEmpty={!byTransition.length}
                height={380}>
                <>
                  <ReportHorizontalBarChart
                    data={byTransition}
                    labelKey="transition"
                    valueKey="median_days"
                    currency={false}
                    height={320}
                    labelWidth={230}
                    colourField="direction"
                    colourMap={DIRECTION_COLOURS}
                  />
                  <div className="d-flex flex-wrap justify-content-center mt-2">
                    {Object.entries(DIRECTION_LABEL).map(([key, label]) => (
                      <span key={key} className="mr-3" style={{fontSize: 11}}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            borderRadius: 2,
                            marginRight: 4,
                            background: DIRECTION_COLOURS[key]
                          }}
                        />
                        {label}
                      </span>
                    ))}
                  </div>
                </>
              </ChartCard>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title text-capitalize">Transition detail</h3>
                </div>
                <div className="card-body table-responsive p-0">
                  <table className="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Move</th>
                        <th className="text-right">Samples</th>
                        <th className="text-right">Median days</th>
                        <th>Direction</th>
                        <th>Reliability</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byTransition.map((t: any) => (
                        <tr key={t.transition}>
                          <td>{t.transition}</td>
                          <td className="text-right">{t.transitions}</td>
                          <td className="text-right">{t.median_days}</td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                background: DIRECTION_COLOURS[t.direction],
                                color: '#fff'
                              }}>
                              {DIRECTION_LABEL[t.direction] || t.direction}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`badge badge-${t.reliable ? 'success' : 'secondary'}`}>
                              {t.reliable ? 'Reliable' : 'Indicative only'}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {!byTransition.length && (
                        <tr>
                          <td colSpan={5} className="text-muted text-center py-4">
                            No transitions in this period.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title text-capitalize">Stalled leads — no movement in 30+ days</h3>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                customStyles={reportTableStyles}
                data={detail?.rows || []}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={detail?.total || 0}
                paginationDefaultPage={applied.page || 1}
                paginationPerPage={applied.limit || 25}
                paginationRowsPerPageOptions={[25, 50, 100]}
                onChangePage={(page) => setApplied({...applied, page})}
                onChangeRowsPerPage={(limit, page) =>
                  setApplied({...applied, limit, page})
                }
                noDataComponent={
                  <div className="text-muted py-4">Nothing is stalled — every lead has moved recently.</div>
                }
                highlightOnHover
                dense
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeadVelocity;
